import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import vueRouter from '@/router/index';
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';
import MessageConst from '@/shared/message-const';
import { TableHeader, ProcessingStatusList, ColumnMenu } from '@/shared/type';
import CommonMessageDialog from '../common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import SystemConst from '@/shared/system-const';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';
import { TableFilterDefs, TableFilters } from '../common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    processingStatusList: ProcessingStatusList[];
    totalCount: number;
    isLoading: boolean;
    processStatusDeleteDialog: ColumnMenu;
    visibleColumnMenu: ColumnMenu;
    pageOptions: DataOptions;
    filterDefs: TableFilterDefs;
    filters: TableFilters;
    isFromDetail: boolean;
};

/**
 * 処理状況コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.PROCESSING_STATUS_LIST;    // テーブル高さ調整用変数
        const router = vueRouter;                                               // 画面遷移用
        const tableHeaderClass: string[] = ['secondary subtitle-2'];            // ヘッダ色
        const page = ref<number>(1);                                            // ページ番号
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                   // 1ページあたりの行数
        const itemsPerPage = ref<number>(linesPerPage[1]);                      // 1ページあたりの行数（初期値）
        const sortBy = ref<string[]>([]);                                       // ソートカラム
        const sortDesc = ref<boolean[]>([]);                                    // ソート順
        const child = ref<any>();                                               // フィルタ一覧
        let isPageLastData = false;                                             // ページの最終データフラグ
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                                       // メッセージダイアログ
        const state: reactiveData = reactive<reactiveData>({                    // リアクティブな変数の宣言
            windowSize: { x: 0, y: 0 },
            processingStatusList: [],                                           // 処理状況の一覧
            totalCount: 0,                                                      // 処理状況の総件数
            isLoading: false,                                                   // テーブルがロード中: true ロード中でない: false
            processStatusDeleteDialog: {},                                      // 処理データの削除ダイアログの表示有無
            visibleColumnMenu: {},                                              // 行メニューの表示有無
            pageOptions: {                                                      // ソート順
                page: 1, itemsPerPage: 0, sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filterDefs: {                                                       // フィルタ定義
                'targetOrganization': { title: '対象組織', types: 'KeyWordPartialMatch' },
                'executionUser': { title: '実行者', types: 'KeyWordPartialMatch' },
                'executionAt': { title: '実行日時', types: 'DatePeriod' },
                'processingStatus': { title: '処理状況', types: 'CheckBox' }
            },
            filters: {},                                                        // フィルタ条件
            isFromDetail: false                                                 // 詳細画面からの遷移フラグ
        });

        const processingStatusTableHeaders: TableHeader[] = [                   // 処理状況ヘッダ情報(テーブルのヘッダ)
            // 対象範囲
            {
                text: '対象範囲',
                value: 'coverage',
                width: '100px',
                class: tableHeaderClass
            },
            // 対象組織
            {
                text: '対象組織',
                value: 'targetOrganization',
                width: '180px',
                class: tableHeaderClass
            },
            // 実行者
            {
                text: '実行者',
                value: 'executionUser',
                width: '180px',
                class: tableHeaderClass
            },
            // 実行日時
            {
                text: '実行日時',
                value: 'executionAt',
                width: '150px',
                class: tableHeaderClass
            },
            // 処理状況
            {
                text: '処理状況',
                value: 'processingStatus',
                width: '150px',
                class: tableHeaderClass
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // フィルタのバリデーション
        // ・対象組織
        const targetOrganizationRule = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '対象組織')
        ];

        // ・実行者
        const executionUserRule = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '実行者')
        ];

        // フィルターに設定された入力文字数上限
        const maxLength = SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH;

        created();
        /**
         * 初期表示
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-list-status');

            // タイトル表示
            ctx.root.$store.commit('setMainTitle', '処理状況');

            // 処理状況一覧をクリアする
            state.processingStatusList = [];

            // 前回のページ情報・ソート情報を復元する
            page.value = ctx.root.$store.state.processingStatusOrganizations.page;
            itemsPerPage.value = ctx.root.$store.state.processingStatusOrganizations.itemsPerPage;
            sortBy.value = ctx.root.$store.state.processingStatusOrganizations.sortBy;
            sortDesc.value = ctx.root.$store.state.processingStatusOrganizations.sortDesc;

            // 前回のフィルタ条件を全て復元する
            state.filters = ctx.root.$store.state.processingStatusOrganizations.filters;

            // storeから詳細画面からの遷移フラグを復元する
            state.isFromDetail = ctx.root.$store.state.processingStatusOrganizations.isFromDetail;

            // フィルタの初期設定を行う
            // 完了するまでawaitで待つ
            await initFilter();

            // 以下で、復元したフィルタ条件から有効なものだけ取得する

            // 有効なフィルタ条件のIDを格納する配列
            const filterIdList = [];
            // 有効なフィルタ条件のフィルタ内容を格納する配列
            const filterParameters = [];

            // 復元したフィルタ条件から有効なもののみについて、IDとフィルタ内容を上記配列に格納する
            for (const key in state.filters) {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    if (val.isEnabled) {
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 有効なフィルタが一つでもある場合
            if (filterIdList.length !== 0) {
                // フィルタ条件をsetAllFiltersでセットする
                // （フィルタの変更によりupdateFilter()が起動し処理状況一覧が取得されるので、updateList()を行う必要はない）
                child.value.setAllFilters(filterIdList, filterParameters);
                // 有効なフィルタが一つもない場合
            } else {
                // 処理状況一覧の取得・設定を行う
                updateList();
            }

            // 詳細画面からの遷移フラグにfalseを設定する
            state.isFromDetail = false;
        }

        /**
         * フィルタ初期設定
         */
        async function initFilter() {
            // 処理状況一覧フィルタ情報取得APIでフィルタ情報を取得する
            const response = await ctx.root.$store.dispatch('processingStatusOrganizations/getFilterConditions');

            // API通信が正常出ない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 取得した情報をフィルタ定義に設定する
            state.filterDefs['processingStatus'].chkItems = response.data.processingStatus;

            // 対象組織、実行者のバリデーションを設定する
            state.filterDefs['targetOrganization'].validator = targetOrganizationRule;
            state.filterDefs['executionUser'].validator = executionUserRule;
        }

        /**
         * フィルタ条件の追加・削除によるイベント
         *
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // state.filtersに変更されたフィルタ条件を保存する
            state.filters = filters;

            // 詳細画面からの遷移以外の場合
            if (!state.isFromDetail) {
                // 1ページへ戻す
                state.pageOptions.page = 1;
                page.value = 1;
            }

            // 処理状況一覧の取得・設定を行う
            updateList();
        }

        /**
         * 一覧のページ操作・ソート操作によるイベント
         * @param options ページ情報・ソート情報
         */
        function updateOptions(options: DataOptions) {
            // v-data-tableよりページ数・１ページあたりの行数を受け取り、
            // state内の変数に設定する。
            // ページ情報をstate内へ保存する
            state.pageOptions = options;

            // ページ情報・ソート情報が変更されていなければ、処理を終了する
            if (page.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                return;
            }

            // ページ番号、1ページごとの項目数、ソートカラム、ソート順を設定する
            page.value = options.page;
            itemsPerPage.value = options.itemsPerPage;
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 処理状況一覧を表示更新する
            updateList();
        }

        /**
         * 処理状況一覧の表示更新
         */
        async function updateList() {

            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // storeのページ情報、ソート情報、フィルタ条件を更新する
            ctx.root.$store.commit('processingStatusOrganizations/setPage', page.value);
            ctx.root.$store.commit('processingStatusOrganizations/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('processingStatusOrganizations/setSortBy', sortBy.value);
            ctx.root.$store.commit('processingStatusOrganizations/setSortDesc', sortDesc.value);
            ctx.root.$store.commit('processingStatusOrganizations/setFilters', state.filters);

            // state内の変数を参照し、以下からAPIのパラメータを作成する。
            //  ページ数
            //  １ページあたりの行数
            //  ソートカラム
            //  ソート順
            //  フィルタ条件
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 処理状況一覧取得APIでデータを取得する
            //  GET /api/processing-status-organizations
            const response = await ctx.root.$store.dispatch('processingStatusOrganizations/getProcesses', params);

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 処理状況一覧のデータをstate.processingStatusListへ設定する
            state.processingStatusList = response.data.processingStatusOrganizations;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.processingStatusList.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 詳細画面遷移処理
         * @param processId 中間処理ID
         */
        function navigationDetail(processId: string) {

            // 処理状況詳細画面へ中間処理IDを渡して遷移する。
            router.push(`/processing-status-organizations/detail/${processId}`);
        }

        /**
         * 処理状況削除処理
         * @param processId 中間処理ID
         */
        async function deleteProcess(processId: string) {

            // 処理データの削除ダイアログを非表示にする
            state.processStatusDeleteDialog[processId] = false;

            // 処理中の処理状況確認APIを呼ぶ
            //   GET /api/processing-status-organizations/{processId}/check
            let response = await ctx.root.$store.dispatch('processingStatusOrganizations/getRunningStatus',
                { 'processId': processId });

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示し、処理を終了する。
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 処理中であれば削除出来ないため、ダイアログを表示して処理を終了する。
            if (response.data.count >= 1) {
                const displayMessage = CommonFunction.replaceArray(MessageConst.E_BECAUSE_OF, ['処理中', '削除']);
                commonRef.value.simpleDisplayMessageDialog(displayMessage)
                return;
            }

            // 処理状況削除APIを呼ぶ
            //   DELETE /api/processing-status-organizations/{processId}
            response = await ctx.root.$store.dispatch('processingStatusOrganizations/deleteProcess',
                { 'processId': processId });

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 削除完了のダイアログを表示する
            commonRef.value.displayMessageDialog(response);

            // ページの最終データフラグがtrueの場合、ページ情報を-1する
            if (isPageLastData) {
                state.pageOptions.page -= 1;
                page.value -= 1;
            }

            // 処理状況一覧を表示更新する
            updateList();

        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize() {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        // テンプレートへ公開
        return {
            tableHeight,
            state,
            commonRef,
            processingStatusTableHeaders,
            page,
            linesPerPage,
            itemsPerPage,
            sortBy,
            sortDesc,
            child,
            tableLoadingText,
            onResize,
            updateFilter,
            updateOptions,
            navigationDetail,
            deleteProcess
        };
    }
});
